import {
  type PartnerAccountHistoryType,
  type PartnerAccountResponse,
  type PartnerStatus,
} from '@/api/routes/partners/partners.api';
import {
  type PartnerInvitableRole,
  type PartnerMember,
  type PartnerMemberInviteRequest,
} from '@/api/routes/partners/partners.members.api';
import {
  type AccountEditTarget,
  type AccountHistoryTone,
  type InviteMemberFormValues,
} from '@/domain/account/types';
import { getActiveLocale, translatedMap } from '@/i18n';
import { onlyDigits } from '@/utils/document';
import { getUserInitials } from '@/utils/user';

export const PARTNER_INVITABLE_ROLES: PartnerInvitableRole[] = ['editor', 'viewer'];

/** Statuses in which the owner edits the account (contract: `PUT /partners/me`). */
const PROFILE_EDIT_STATUSES: PartnerStatus[] = ['pending_review', 'rejected', 'approved'];

export const memberRoleLabel = translatedMap('domain.account.utils', {
  owner: 'role.owner',
  editor: 'role.editor',
  viewer: 'role.viewer',
} as const);

/**
 * "Editar perfil": an approved account edits here, with the document locked; in review it goes
 * through the onboarding edit (P05/P06). Only the owner edits, as the API decides.
 */
export const getAccountEditTarget = (
  account?: PartnerAccountResponse | null,
): AccountEditTarget => {
  const partner = account?.partner;
  if (!partner || account.role !== 'owner' || !PROFILE_EDIT_STATUSES.includes(partner.status)) {
    return null;
  }
  return partner.status === 'approved' ? 'account' : 'onboarding';
};

/** The document and the legal name the team checked stay locked once approved. */
export const isDocumentLocked = (status: PartnerStatus) => status === 'approved';

/** Inviting, withdrawing and removing are the owner's, with the account approved (as the API). */
export const canManageMembers = (account?: PartnerAccountResponse | null) =>
  account?.role === 'owner' && account.partner?.status === 'approved';

/** Only editors and viewers can be removed; the owner changes through the Bodyprint team. */
export const canRemoveMember = (member: Pick<PartnerMember, 'role'>) => member.role !== 'owner';

/**
 * Shows enough of the document to recognize it, not to copy it:
 * `12.345.678/••••-90` (CNPJ) or `123.•••.•••-25` (CPF).
 */
export const maskTaxId = (taxId: string): string => {
  const digits = onlyDigits(taxId);
  if (digits.length === 14) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/••••-${digits.slice(12)}`;
  }
  if (digits.length === 11) return `${digits.slice(0, 3)}.•••.•••-${digits.slice(9)}`;
  return '•••';
};

export const getHistoryTone = (type: PartnerAccountHistoryType): AccountHistoryTone => {
  if (type === 'approved' || type === 'reactivated' || type === 'claimed') return 'ok';
  if (type === 'rejected' || type === 'suspended') return 'error';
  return 'neutral';
};

const formatDate = (iso: string, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(getActiveLocale(), options)
    .format(new Date(iso))
    .replace('.', '')
    .replace(/ de /g, ' ');

/** `12 out 2026 · 09:40` for a timeline line. */
export const formatHistoryDateTime = (iso: string) =>
  `${formatDate(iso, { day: 'numeric', month: 'short', year: 'numeric' })} · ${formatDate(iso, {
    hour: '2-digit',
    minute: '2-digit',
  })}`;

/** `12 out 2026` for "desde" and "vale até". */
export const formatAccountDate = (iso: string) =>
  formatDate(iso, { day: 'numeric', month: 'short', year: 'numeric' });

export const getMemberName = (member: Pick<PartnerMember, 'firstName' | 'lastName'>) =>
  [member.firstName, member.lastName].filter(Boolean).join(' ');

export const getMemberInitials = (member: Pick<PartnerMember, 'firstName' | 'lastName'>) =>
  getUserInitials({ firstName: member.firstName, lastName: member.lastName ?? undefined });

/** `https://www.correpb.com.br/` → `correpb.com.br`, for the profile line. */
export const formatWebsite = (url: string) =>
  url
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '');

export const toMemberInviteRequest = (
  values: InviteMemberFormValues,
): PartnerMemberInviteRequest => ({ email: values.email.trim(), role: values.role });
